import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Bell } from "lucide-react";

export default function RestockRequestsTile() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/restock-requests")
      .then((r) => setRequests(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of requests) {
      const key = `${r.category || "?"}|${r.size || "?"}|${r.curator || ""}|${(r.brand || "").toLowerCase()}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          category: r.category,
          size: r.size,
          curator: r.curator,
          brand: r.brand,
          color: r.color,
          ref_title: r.ref_title,
          emails: new Set(),
          latest: r.created_at,
        });
      }
      const entry = map.get(key);
      entry.emails.add(r.email);
      if (r.created_at > entry.latest) entry.latest = r.created_at;
    }
    return [...map.values()]
      .map((g) => ({ ...g, count: g.emails.size }))
      .sort((a, b) => b.count - a.count);
  }, [requests]);

  return (
    <div className="brut p-4 bg-white mt-6" data-testid="restock-requests-tile">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bell size={20} strokeWidth={3} />
          <h3 className="font-display text-2xl uppercase">Restock demand</h3>
          {requests.length > 0 && (
            <span className="tag-sticker bg-pink text-white text-sm">
              {requests.length}
            </span>
          )}
        </div>
        <span className="text-xs opacity-70">
          shoppers waiting — list a match and we'll auto-email them
        </span>
      </div>

      {loading ? (
        <div className="text-sm opacity-60 text-center py-6">Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="text-sm opacity-60 text-center py-6">
          No open restock requests. Quiet on the wishlist front.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {grouped.map((g) => (
            <div
              key={g.key}
              className="brut-sm p-3 bg-paper flex items-start justify-between gap-3"
              data-testid={`restock-group-${g.category}-${g.size}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-display text-base uppercase truncate">
                  {g.category || "?"} · size {g.size || "?"}
                  {g.curator && (
                    <span className="ml-2 text-sm normal-case opacity-80">
                      {g.curator === "boggy" ? "🐸 Boggy" : "🦊 Fox"}
                    </span>
                  )}
                </div>
                <div className="text-xs opacity-70 truncate">
                  {g.brand ? `Brand: ${g.brand} · ` : ""}
                  Last: {g.ref_title}
                </div>
              </div>
              <span
                className="tag-sticker bg-orange text-white text-sm"
                data-testid={`restock-count-${g.category}-${g.size}`}
              >
                {g.count} {g.count === 1 ? "person" : "people"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
